import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import {
  BloombergLogo,
  BloombergLogoUK,
  BloombergLogoWeekend,
  Exit,
  Hamburger,
  UserAccountProfile,
} from '@bloomberg/media-ui-icons';

import { SignInButtons } from '../LogoBar';
import { DEFAULT_USER } from '../Nav';
import { useNavContext } from '../Nav/NavContext';
import { LANGUAGES, REGIONS, THEMES, User } from '../types';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './LogoBarMobile.module.css';

export type LogoBarMobileProps = GlobalProps & {
  isMenuOpen?: boolean;
  isAccountMenuOpen?: boolean;
  isWeekend?: boolean;
  language?: LANGUAGES;
  name?: string;
  region?: REGIONS;
  theme?: THEMES;
  toggleMenu?: () => void;
  toggleAccountMenu?: () => void;
  user?: User;
  onSignOut?: () => void;
  trackingFunction: (eventLabel: string) => void;
  subscriptionUrl: string;
  upgradeUrl: string;
  countryCode?: string;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
};

export type LogoProps = {
  isWeekend?: boolean;
  trackingFunction: (eventLabel: string) => void;
};

export type UserProps = {
  user?: User;
  theme?: THEMES;
};

type SignInButtonProps = {
  trackingFunction: (eventLabel: string) => void;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
};

export const SignInButton = ({
  trackingFunction,
  handleShowSignInModal,
  setSignInButtonRef,
}: SignInButtonProps) => {
  const ref = useRef(null);
  const translateMessage = useTranslateMessage();

  useEffect(() => {
    if (ref.current) {
      setSignInButtonRef?.((prevState) => {
        return {
          ...prevState,
          mobileSignInButton: ref.current || undefined,
        };
      });
    }
  }, []);

  return (
    <>
      <button
        aria-label={translateMessage({
          id: 'media-ui-nav_signin',
          defaultMessage: 'Sign In',
        })}
        data-testid="sign-in-button"
        ref={ref}
        className={styles.signInButton}
        onClick={() => {
          trackingFunction('signIn');
          handleShowSignInModal?.();
        }}
      >
        <UserAccountProfile className={styles.userAccountProfile} />
      </button>
    </>
  );
};

const SubscribeButton = ({
  trackingFunction,
  subscriptionUrl,
}: {
  trackingFunction: (eventLabel: string) => void;
  subscriptionUrl: string;
}) => {
  return (
    <a
      className={styles.subscribeButtonContainer}
      href={`${subscriptionUrl}?in_source=nav-mobileweb`}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackingFunction('subscribe')}
    >
      <span className={styles.subscribeButton}>
        <TranslatedMessage
          id="media-ui-nav_subscribe"
          defaultMessage="Subscribe"
        />
      </span>
    </a>
  );
};

const UpgradeButton = ({
  trackingFunction,
  upgradeUrl,
}: {
  trackingFunction: (eventLabel: string) => void;
  upgradeUrl: string;
}) => {
  return (
    <a
      className={styles.subscribeButtonContainer}
      href={upgradeUrl}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackingFunction('upgrade')}
    >
      <span className={styles.subscribeButton}>
        <TranslatedMessage id="media-ui-nav_upgrade" defaultMessage="Upgrade" />
      </span>
    </a>
  );
};

const LogoBarAnonymousUser = ({
  trackingFunction,
  subscriptionUrl,
  handleShowSignInModal,
  setSignInButtonRef,
}: LogoBarMobileProps) => {
  return (
    <div className={styles.right}>
      <SubscribeButton
        trackingFunction={trackingFunction}
        subscriptionUrl={subscriptionUrl}
      />
      <SignInButton
        trackingFunction={trackingFunction}
        handleShowSignInModal={handleShowSignInModal}
        setSignInButtonRef={setSignInButtonRef}
      />
    </div>
  );
};

const LogoBarLoggedInUser = ({
  trackingFunction,
  toggleAccountMenu,
  subscriptionUrl,
}: LogoBarMobileProps) => {
  const translateMessage = useTranslateMessage();

  return (
    <div className={styles.right}>
      <SubscribeButton
        trackingFunction={trackingFunction}
        subscriptionUrl={subscriptionUrl}
      />
      <button
        className={styles.userAccountButton}
        onClick={toggleAccountMenu}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_accountMenu',
          defaultMessage: 'Account Menu',
        })}
        data-testid="user-options-button"
        role="button"
      >
        <UserAccountProfile className={styles.userAccountProfile} />
      </button>
    </div>
  );
};

const AccountLoggedInUser = ({ toggleAccountMenu }: LogoBarMobileProps) => {
  const translateMessage = useTranslateMessage();

  return (
    <div className={styles.right}>
      <button
        className={styles.exitButton}
        onClick={toggleAccountMenu}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_exitButton',
          defaultMessage: 'Exit',
        })}
        data-testid="exit-button"
        role="button"
      >
        <Exit className={styles.exitIcon} />
      </button>
    </div>
  );
};

const LogoBarUpgradeUser = ({
  trackingFunction,
  toggleAccountMenu,
  upgradeUrl,
}: LogoBarMobileProps) => {
  const translateMessage = useTranslateMessage();

  return (
    <div className={styles.right}>
      <UpgradeButton
        trackingFunction={trackingFunction}
        upgradeUrl={upgradeUrl}
      />
      <button
        className={styles.userAccountButton}
        onClick={toggleAccountMenu}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_accountMenu',
          defaultMessage: 'Account Menu',
        })}
        data-testid="user-options-button"
        role="button"
      >
        <UserAccountProfile className={styles.userAccountProfile} />
      </button>
    </div>
  );
};

const AccountUpgradeUser = ({ toggleAccountMenu }: LogoBarMobileProps) => {
  const translateMessage = useTranslateMessage();

  return (
    <div className={styles.right}>
      <button
        className={styles.exitButton}
        onClick={toggleAccountMenu}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_exitButton',
          defaultMessage: 'Exit',
        })}
        data-testid="exit-button"
        role="button"
      >
        <Exit className={styles.exitIcon} />
      </button>
    </div>
  );
};

const LogoBarSubscribedUser = ({ toggleAccountMenu }: LogoBarMobileProps) => {
  const translateMessage = useTranslateMessage();

  return (
    <div className={styles.right}>
      <button
        className={styles.userAccountButton}
        onClick={toggleAccountMenu}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_accountMenu',
          defaultMessage: 'Account Menu',
        })}
        data-testid="user-options-button"
        role="button"
      >
        <UserAccountProfile className={styles.userAccountProfile} />
      </button>
    </div>
  );
};

const AccountSubscribedUser = ({ toggleAccountMenu }: LogoBarMobileProps) => {
  const translateMessage = useTranslateMessage();

  return (
    <div className={styles.right}>
      <button
        className={styles.exitButton}
        onClick={toggleAccountMenu}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_exitButton',
          defaultMessage: 'Exit',
        })}
        data-testid="exit-button"
        role="button"
      >
        <Exit className={styles.exitIcon} />
      </button>
    </div>
  );
};

const LogoBarRight = (props: LogoBarMobileProps) => {
  const { user = DEFAULT_USER, isAccountMenuOpen = false } = props;

  const userIsAnonymous = !user?.isLoggedIn;
  const userIsLoggedIn = user?.isLoggedIn && !user.isSubscriber;
  const userIsSubscriber = user?.isLoggedIn && user.isSubscriber;
  const userHasUpgrade = user?.showUpgrade;

  if (userIsAnonymous && !isAccountMenuOpen) {
    return <LogoBarAnonymousUser {...props} />;
  }
  if (userIsLoggedIn && !isAccountMenuOpen) {
    return <LogoBarLoggedInUser {...props} />;
  }
  if (userIsLoggedIn && isAccountMenuOpen) {
    return <AccountLoggedInUser {...props} />;
  }
  if (userHasUpgrade && !isAccountMenuOpen) {
    return <LogoBarUpgradeUser {...props} />;
  }
  if (userHasUpgrade && isAccountMenuOpen) {
    return <AccountUpgradeUser {...props} />;
  }
  if (userIsSubscriber && !isAccountMenuOpen) {
    return <LogoBarSubscribedUser {...props} />;
  }
  if (userIsSubscriber && isAccountMenuOpen) {
    return <AccountSubscribedUser {...props} />;
  }

  return null;
};

const BloombergLogoComponent = (props: LogoProps) => {
  const { isWeekend, trackingFunction } = props;
  const { region } = useNavContext();
  const href = region === REGIONS.JAPAN ? 'https://www.bloomberg.co.jp' : '/';

  if (isWeekend) {
    return (
      <div className={styles.logoWeekend}>
        <a
          className={styles.logoLink}
          href="/"
          onClick={() => trackingFunction('bloomberg')}
        >
          <BloombergLogoWeekend />
          <BloombergLogo />
        </a>
      </div>
    );
  }

  if (region === REGIONS.UK) {
    return (
      <div className={styles.logoUK}>
        <a
          className={styles.logoLink}
          href="/uk"
          onClick={() => trackingFunction('bloomberg')}
        >
          <BloombergLogoUK />
        </a>
      </div>
    );
  }

  return (
    <div className={styles.logo}>
      <a
        className={styles.logoLink}
        href={href}
        onClick={() => trackingFunction('bloomberg')}
      >
        <BloombergLogo />
      </a>
    </div>
  );
};

export const Logo = (props: LogoProps) => {
  const { isWeekend, trackingFunction } = props;

  return (
    <BloombergLogoComponent
      trackingFunction={trackingFunction}
      isWeekend={isWeekend}
    />
  );
};

export const LogoBarMobile = React.forwardRef<
  HTMLDivElement,
  LogoBarMobileProps
>(function LogoBarMobileComponent(props: LogoBarMobileProps, ref) {
  const {
    isWeekend,
    name = 'logo-bar-mobile',
    theme = THEMES.LIGHT,
    isMenuOpen = false,
    toggleMenu = () => {},
    trackingFunction,
  } = props;

  const { locale } = useNavContext();
  const showLoginMobile = locale === 'en';
  const componentName = useSlug(name);
  const translateMessage = useTranslateMessage();

  const cx = classes(
    [
      styles.logoBarMobileContainer,
      {
        [styles.isWeekend]: isWeekend,
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
      },
    ],
    {
      name,
    }
  );

  return (
    <div className={cx} ref={ref} data-component={componentName}>
      <div className={styles.bar}>
        <div className={styles.left}>
          <button
            className={styles.menuMobile}
            aria-label={translateMessage({
              id: 'media-ui-nav_arialabel_mobileMenuButton',
              defaultMessage: 'Mobile Menu Button',
            })}
            onClick={toggleMenu}
            data-testid="mobile-menu-button"
            role="button"
          >
            {isMenuOpen ? (
              <Exit className={styles.exitIcon} />
            ) : (
              <Hamburger className={styles.hamburger} />
            )}
          </button>
          <Logo trackingFunction={trackingFunction} isWeekend={isWeekend} />
        </div>
        {showLoginMobile && <LogoBarRight {...props} />}
      </div>
    </div>
  );
});
